/**
 * Shared primitive schemas. Every boundary in the system — client request validation,
 * Worker request validation, DO messages, queue payloads — validates against the same
 * Zod schemas, so client and server cannot drift (docs/07-tech-architecture.md §5).
 */

import {
  CEFR_SUBLEVELS,
  DIALECTS,
  DISPUTE_REASONS,
  INTERFACE_LOCALES,
  ITEM_TYPES,
  LANGUAGES,
  MODALITIES,
  NOTIFICATION_TYPES,
  PILLARS,
  SESSION_KINDS,
} from '@vibelang/core';
import { z } from 'zod';

/** ULID: 26 chars, Crockford base32. Client-generated, which is what makes writes idempotent. */
export const UlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'must be a ULID');

/**
 * A media URL the client can fetch: either absolute, or root-relative.
 *
 * The API serves every recording as `/v1/audio/<key>` — relative on purpose, because the
 * only environment-independent thing to store is the R2 key and the host is whatever
 * host the client is already talking to. This was typed `z.string().url()` in fourteen
 * places, which the real responses have never satisfied; nothing noticed because the
 * clients validate requests and not responses, so the only consumer the mismatch would
 * have reached is someone trusting our published OpenAPI.
 *
 * A regex rather than a refinement, so the shape stays *inspectable*: the fixture
 * generator and the OpenAPI converter both read a pattern and neither can see inside a
 * `.refine()`. A refinement here produced fixtures that failed the very schema they were
 * generated from.
 */
/**
 * An id that may name authored content.
 *
 * Content ids are slugs — `itm_es_mx_hola`, `les_es_mx_primeras_01`, `gra_es_mx_gustar` —
 * authored server-side, stable across releases, and readable on purpose: the validator,
 * the tests, the audio keys and the review console all refer to them by name. A ULID is
 * the right rule for a *client-generated* id, which is what makes a retried write
 * idempotent, and applying that rule to authored content is a category error. It was
 * applied anyway, in sixteen places, so every content response the API can return fails
 * its own contract.
 *
 * Both forms are accepted rather than only the slug, because some of these ids genuinely
 * can be either: a learner's own role-play carries a client ULID, and a card can point at
 * an item from either source.
 *
 * Sixteen was not all of them. Three more turned up later — `createSession`'s `refId` and
 * the `lessonId`/`scenarioId` path params — and the first of those was not cosmetic: it
 * made scenarios unstartable in production. The Worker looks the ref up with
 * `SELECT ... FROM lessons WHERE id = ?`, so it wanted the content id all along, and the
 * body schema rejected every scenario id the same API had just served: `400 refId must be
 * a ULID`.
 *
 * Nothing caught it, and the reason is worth keeping: `apps/api/test/helpers.ts` seeds
 * lessons and scenarios with `testUlid()`, so the entire session suite ran against a
 * shape the published corpus has never had. The fixture was the only place in the system
 * where those ids were ULIDs.
 */
export const ContentIdSchema = z
  .string()
  .regex(
    /^(?:[0-9A-HJKMNP-TV-Z]{26}|[a-z]{2,4}(?:_[a-z0-9]+)+)$/,
    'must be a content id or a ULID',
  );

export const MediaUrlSchema = z
  .string()
  .regex(/^(?:https?:\/\/\S+|\/\S*)$/, 'must be an absolute http(s) URL or a root-relative path');

export const LanguageSchema = z.enum(LANGUAGES);
export const DialectSchema = z.enum(DIALECTS);
export const CefrSchema = z.enum(CEFR_SUBLEVELS);
export const ItemTypeSchema = z.enum(ITEM_TYPES);
export const PillarSchema = z.enum(PILLARS);
export const ModalitySchema = z.enum(MODALITIES);
export const SessionKindSchema = z.enum(SESSION_KINDS);
export const NotificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export const DisputeReasonSchema = z.enum(DISPUTE_REASONS);
export const SessionModeSchema = z.enum(['voice', 'text']);

export const EpochMsSchema = z.number().int().nonnegative();
export const Score100Schema = z.number().min(0).max(100);
export const Unit01Schema = z.number().min(0).max(1);

/** IETF BCP 47 subset we actually ship UI in. */
/**
 * Derived from the shared list rather than repeated here: the clients narrow a platform
 * locale to this set, and a second copy of the list is how one of them came to send
 * `en-US` to an enum that only accepts `en` (packages/core/src/i18n/interface-locale.ts).
 */
export const InterfaceLocaleSchema = z.enum(INTERFACE_LOCALES);

/** IANA timezone name. Validated loosely; the authoritative check is server-side. */
export const TimezoneSchema = z.string().min(1).max(64);

/** `YYYY-MM-DD` in the learner's own timezone. Streaks are computed on these. */
export const LocalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const ScoreStatusSchema = z.enum([
  'scored',
  'reduced',
  'withheld',
  'unavailable',
  'pending',
]);

/**
 * Accuracy and intonation are nullable: they need phone-level alignment and a pitch
 * contour respectively, and without a pronunciation-assessment engine neither can be
 * measured. Null means not measured, and every client renders it as absent rather than
 * as zero (packages/core/src/types.ts).
 */
/*
 * Every dimension is nullable, because any of them can go unmeasured.
 *
 * Two were, and three were not, so the aggregate report filled the other three with
 * `?? 0` to satisfy this shape. A learner who typed every turn of a lesson was then told
 * their fluency was 0 out of 100 — a judgement about speech they never produced, and
 * exactly the invented precision `ScoreStatus.withheld` exists to avoid.
 *
 * Null means we did not measure it. Zero means we did.
 */
export const ScoreDimensionsSchema = z.object({
  accuracy: Score100Schema.nullable(),
  fluency: Score100Schema.nullable(),
  completeness: Score100Schema.nullable(),
  intonation: Score100Schema.nullable(),
  confidence: Score100Schema.nullable(),
});

export const ScoreBandSchema = z.object({
  id: z.enum(['native_like', 'great', 'good', 'getting_there', 'keep_going']),
  label: z.string(),
});

export const PersonaIdSchema = z.enum(['mika', 'dani', 'sol', 'rex', 'vex', 'nakamura']);

export const CorrectionStyleSchema = z.enum(['inline', 'after_turn', 'end_of_session']);
export const ScaffoldingSchema = z.enum(['l1_supported', 'target_only']);

export const TierSchema = z.enum(['free', 'plus', 'pro']);

export const CursorPageSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
}
